<script setup lang="ts">
import { ref } from 'vue';
import { useProjectStore } from '/@features/project';
import { projectsLinkFromModuleIds } from '/@features/project/project.tools';
import { Project, ProjectProperty } from '/@features/project/project.types';
import { ProjectProperties } from '/@types/ids';
import { ResourceEvent } from '/@types/resources';

const props = defineProps<{
  event: ResourceEvent;
}>();

const projectStore = useProjectStore();

const showProjectInfo = ref(false);
const project = ref<Project | null>(null);
const projectProperties = ref<Map<number, ProjectProperty>>(new Map());
const projectInfo = ref<Array<{ label: string; value: string; large?: boolean }>>([]);

async function showProjectInfoData(projectId: number) {
  showProjectInfo.value = true;

  if (project.value == null || project.value.id !== projectId) {
    project.value = null;
    projectInfo.value = [];

    Promise.all([
      projectStore.loadProjectById(projectId),
      projectStore.loadProjectProperties(projectId),
    ]).then(([proj, properties]) => {
      project.value = proj;
      projectProperties.value = properties;

      projectInfo.value = [
        { label: 'Status', value: project.value.statusName },
        { label: 'Saksbehandler', value: project.value.caseworkerName },
        { label: 'Adresse', value: project.value.address },
        { label: 'Oppdragsgiver', value: project.value.contactCompanyName },
        { label: 'Kontrakts-/ordrenummer', value: project.value.ref1 },
        { label: 'Internnummer', value: project.value.ref2 },
        { label: 'Ansvarlig montør', value: project.value.installer },
        { label: 'Avtale opprettet av', value: props.event.createdByUserName },
        ...[...properties]
          .filter(([id]) =>
            [ProjectProperties.RessursPrioritet, ProjectProperties.RessursKommentar].includes(id),
          )
          .map(([, p]) => ({ label: p.name, value: p.value })),
        { label: 'Beskrivelse', value: project.value.description, large: true },
      ];
    });
  }
}
</script>

<template>
  <div class="dl__group dl__group--large dl__group--actions">
    <h2 class="dl__topic">Prosjekt</h2>
    <p class="dl__description">{{ event.projectName }}</p>

    <div class="dl__actions">
      <df-button
        v-on:click="
          showProjectInfo
            ? (showProjectInfo = !showProjectInfo)
            : showProjectInfoData(event.projectId)
        "
        :active="showProjectInfo"
      >
        <template v-slot:icon>
          <df-icon code="f05a" />
        </template>
      </df-button>

      <template v-if="event.projectAccess && event.projectModuleIds.length > 0">
        <!-- This is a single item loop so we prevent calling the function twice (once for v-if and another for :href) -->
        <df-button
          v-for="link in [
            projectsLinkFromModuleIds(event.projectModuleIds, event.projectId),
          ].filter((x) => x)"
          target="_blank"
          :href="link"
        >
          <template v-slot:icon>
            <df-icon code="f08e" />
          </template>
        </df-button>
      </template>
    </div>
  </div>

  <template v-if="showProjectInfo && projectInfo.length === 0">
    <div class="dl__group dl__group--large dl__group--horizontal">
      <div class="dl__description">
        <div class="loading-icon"></div>
      </div>
    </div>
  </template>

  <template v-else-if="showProjectInfo">
    <template v-for="entry in projectInfo" :key="entry.label">
      <div class="dl__group dl__group--sub" :class="{ 'dl__group--large': entry.large }">
        <h2 class="dl__topic">{{ entry.label }}</h2>
        <p class="dl__description">{{ entry.value || '-' }}</p>
      </div>
    </template>
  </template>
</template>
